<template>
  <BittsPopover
    :show-menu="filterVisible"
    placement="bottomLeft"
    v-click-away="hideFilter"
    @menu-visible="onFilterVisibilityToggled"
  >
    <component :is="tooltipOrDiv">
      <BittsFilterButton
        :active="!!count"
        :count="!suppressCount ? count : undefined"
        :disabled
        :is-open="filterVisible"
        :label-text="name"
        :show-icon-only="onlyShowIcon"
        :prefix-icon="icon"
        variant="simple"
        @click="() => (filterVisible = !filterVisible)"
      />
      <template #title>
        {{ name }}
      </template>
    </component>
    <template #content>
      <BittsSimpleNumberFilter
        v-if="dataType === BittsFilterDataType.NUMBER"
        :currency
        :value="value as [number, number]"
        @range-changed="onFilterChanged"
      />
      <BittsSimpleDateFilter
        v-else-if="dataType === BittsFilterDataType.DATETIME"
        :value="value as [string, string]"
        @date-changed="onFilterChanged"
      />
      <BittsSimpleObjectFilter
        v-else-if="dataType === BittsFilterDataType.OBJECT"
        :value="value as (string | number)[]"
        :options
        :searchable
        :search="filterSearch"
        :loading
        :placeholder
        @search-applied="onSearchApplied"
        @filter-changed="onFilterChanged"
        @filter-selected="
          (value: string | number, checked: boolean) =>
            emit('filter-object-selected', value, filter, checked)
        "
      >
        <template #option-prefix-icon="slotProps">
          <slot name="option-prefix-icon" v-bind="slotProps" />
        </template>
        <template #option-suffix-icon="slotProps">
          <slot name="option-suffix-icon" v-bind="slotProps" />
        </template>
      </BittsSimpleObjectFilter>
      <BittsSimpleRadioFilter
        v-else-if="dataType === BittsFilterDataType.RADIO"
        :value="value as string[]"
        :options
        :loading
        :search="filterSearch"
        :searchable
        :placeholder
        @filter-changed="onFilterChanged"
        @search-applied="onSearchApplied"
        @tooltip-clicked="() => emit('tooltip-clicked')"
      />
      <BittsSimpleTreeFilter
        v-else-if="dataType === BittsFilterDataType.TREE"
        :options="convertOptionsToTreeData(options)"
        :initial-expanded-nodes="{}"
        :value="value as string[]"
        :loading
        :search="filterSearch"
        :searchable
        :placeholder
        @search-applied="onSearchApplied"
        @filter-changed="onFilterChanged"
      />
      <BittsDivider class="m-0" />
      <footer class="flex items-center justify-stretch p-12 pb-8 gap-8">
        <BittsButton
          :disabled="loading"
          text="Clear"
          size="x-small"
          type="neutral"
          class="w-full"
          @click="onFilterCleared"
        />
        <BittsButton
          :disabled="loading"
          text="Apply"
          size="x-small"
          class="w-full"
          @click="onFilterApplied"
        />
      </footer>
    </template>
  </BittsPopover>
</template>

<script setup lang="ts">
import { Nullable } from '@crossbeam/types';

import { computed, ref, watchEffect } from 'vue';
import { directive as vClickAway } from 'vue3-click-away';

import BittsButton from '../BittsButton/BittsButton.vue';
import BittsDivider from '../BittsDivider/BittsDivider.vue';
import BittsFilterButton from '../BittsFilterButton/BittsFilterButton.vue';
import BittsPopover from '../BittsPopover/BittsPopover.vue';
import BittsTooltip from '../BittsTooltip/BittsTooltip.vue';
import { TreeNodeType } from '../BittsTree/BittsBaseTree.vue';
import {
  BittsFilterDataType,
  BittsFilterValue,
  BittsIcon,
  BittsSimpleFilterOption,
} from '../types';

import BittsSimpleDateFilter from './BittsSimpleDateFilter/BittsSimpleDateFilter.vue';
import BittsSimpleNumberFilter from './BittsSimpleNumberFilter/BittsSimpleNumberFilter.vue';
import BittsSimpleObjectFilter from './BittsSimpleObjectFilter/BittsSimpleObjectFilter.vue';
import BittsSimpleRadioFilter from './BittsSimpleRadioFilter/BittsSimpleRadioFilter.vue';
import BittsSimpleTreeFilter from './BittsSimpleTreeFilter/BittsSimpleTreeFilter.vue';

const {
  currency = 'USD',
  dataType = BittsFilterDataType.OBJECT,
  disabled = false,
  name = '',
  filter,
  id = 0,
  loading = false,
  buttonIcon = null,
  filterValue,
  options = undefined,
  onlyShowIcon = false,
  searchable = true,
  placeholder,
  suppressCount = false,
} = defineProps<{
  currency?: string;
  dataType?: BittsFilterDataType;
  disabled?: boolean;
  filter: string;
  id?: number;
  loading?: boolean;
  name?: string;
  buttonIcon?: Nullable<BittsIcon>;
  filterValue?: BittsFilterValue;
  onlyShowIcon?: boolean;
  placeholder?: string;
  searchable?: boolean;
  options?: BittsSimpleFilterOption[];
  suppressCount?: boolean;
}>();
const emit = defineEmits<{
  (
    e: 'filter-applied',
    val: BittsFilterValue | undefined,
    filter: string,
  ): void;
  (
    e: 'filter-visibility-toggled',
    open: boolean,
    val: string,
    id: number,
  ): void;
  (e: 'search-applied', val: string, id: number, query: string): void;
  (
    e: 'filter-object-selected',
    value: string | number,
    filter: string,
    checked?: boolean,
  ): void;
  (e: 'filter-cleared' | 'filter-changed', info: string): void;
  (e: 'tooltip-clicked'): void;
  (e: 'tree-updated', tree: TreeNodeType[]): void;
}>();

const icon = computed<BittsIcon>(() => {
  if (buttonIcon) return buttonIcon;
  switch (dataType) {
    case BittsFilterDataType.NUMBER:
      return ['fak', 'usd'];
    case BittsFilterDataType.DATETIME:
      return ['fak', 'calendar'];
    default:
      return ['fak', 'partner'];
  }
});

const value = ref(filterValue);
const filterVisible = ref(false);

const filterSearch = ref<string>('');
function onSearchApplied(query: string) {
  filterSearch.value = query;
  emit('search-applied', filter, id, filterSearch.value);
}

const count = computed(() => {
  if (dataType === BittsFilterDataType.OBJECT) {
    return (value.value as (string | number)[])?.length;
  }
  if (dataType === BittsFilterDataType.TREE) {
    return countLeafNodes(filterValue as string[], options || []);
  }
  return filterValue?.length;
});

const tooltipOrDiv = computed(() => (onlyShowIcon ? BittsTooltip : 'div'));

function hideFilter() {
  filterVisible.value = false;
}
function onFilterApplied() {
  emit('filter-applied', value.value, filter);
  hideFilter();
  onFilterVisibilityToggled({ visible: false });
}
function onFilterCleared() {
  value.value = undefined;
  emit('filter-cleared', filter);
  hideFilter();
  onFilterVisibilityToggled({ visible: false });
}
function onFilterChanged(newVal?: BittsFilterValue) {
  value.value = newVal;
}
function onFilterVisibilityToggled({ visible }: { visible: boolean }) {
  emit('filter-visibility-toggled', visible, filter, id);
  if (!visible) filterSearch.value = '';
}

watchEffect(() => {
  if (!filterVisible.value) onFilterChanged(filterValue);
});

// Helper functions
function convertOptionsToTreeData(
  options: BittsSimpleFilterOption[] | undefined,
): TreeNodeType[] {
  if (!options) return [];
  return options.map(convertNodeRecursively);
}

function convertNodeRecursively(node: BittsSimpleFilterOption): TreeNodeType {
  return {
    id: node.value,
    label: node.label,
    checked: node.checked || false,
    children: node.children
      ? node.children.map(convertNodeRecursively)
      : undefined,
  };
}

// Helper function to count only leaf nodes from selected values
function countLeafNodes(
  selectedValues: string[],
  options: BittsSimpleFilterOption[],
): number {
  if (!selectedValues?.length) return 0;

  const allLeafNodes = new Set<string>();

  function collectLeafNodes(options: BittsSimpleFilterOption[]) {
    options.forEach((option) => {
      if (!option.children || option.children.length === 0) {
        // This is a leaf node
        allLeafNodes.add(option.value as string);
      } else {
        // This is a parent node, check its children
        collectLeafNodes(option.children);
      }
    });
  }
  collectLeafNodes(options);

  // Only count selected values that are leaf nodes
  return selectedValues.filter((value) => allLeafNodes.has(value)).length;
}
</script>
